import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usersService } from '@/services/users'
import { handleApiError } from '@/utils/errors'

interface Disable2FAModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function Disable2FAModal({ open, onOpenChange, onSuccess }: Disable2FAModalProps) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    setError(null)
    setIsSubmitting(true)
    try {
      await usersService.disable2FA()
      onSuccess()
      onOpenChange(false)
    } catch (err) {
      setError(handleApiError(err).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/50" />
        <Dialog.Content className="bg-card border-border data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border p-6 shadow-lg">
          <Dialog.Title className="text-foreground text-lg font-semibold">
            {t('profile.disable2FA.title')}
          </Dialog.Title>
          <Dialog.Description className="text-muted-foreground mt-1 text-sm">
            {t('profile.disable2FA.description')}
          </Dialog.Description>
          <div className="mt-4 flex flex-col gap-4">
            {error && (
              <p className="text-destructive text-sm" role="alert">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="border-border text-foreground hover:bg-muted rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                >
                  {t('profile.editProfile.cancel')}
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {isSubmitting
                  ? t('profile.disable2FA.submitting')
                  : t('profile.disable2FA.confirm')}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
